import { useEffect, useRef, useState } from 'react';
import { Plus } from '@phosphor-icons/react';
import { AIRCRAFT_TYPES, aircraftType } from '../data/aircraft';
import { airportById } from '../data/airports';
import { HUB_SPECIALIZATION_LABELS, hubBuildCost, routeLaunchCost } from '../simulation/economy';
import { idleDeliveredAircraft, isAircraftDelivered } from '../simulation/leasing';
import type { AiRouteTactic, AiStrategy, GameState } from '../game/types';
import { tutorialSteps } from '../game/tutorial';
import { integer, kilometers, money, percent } from '../ui/format';

interface Props {
  game: GameState;
  selectedAirportId?: string;
  selectedRouteId?: string;
  selectedAiRouteId?: string;
  routeOriginId?: string;
  routeAircraftId?: string;
  onBeginRoute: (originId: string) => void;
  onCancelRoute: () => void;
  onRouteAircraftChange: (aircraftId: string) => void;
  onBuildHub: (airportId: string) => void;
  onViewRoutes: (airportId: string) => void;
  onCloseRoute: (routeId: string) => void;
  onAssignAircraft: (routeId: string, aircraftId: string) => void;
  onUnassignAircraft: (routeId: string, aircraftId: string) => void;
  onSetRouteFareRatio?: (routeId: string, fareRatio: number) => void;
  onViewAircraft?: (aircraftId: string) => void;
  onAirportSelect?: (airportId: string) => void;
  onRouteSelect?: (routeId: string) => void;
  onAiRouteSelect?: (managerId: string, routeId: string) => void;
  onOpenRoute?: (originId: string, destinationId: string, aircraftId: string) => void;
}

const MOBILE_SHEET_QUERY = '(max-width: 820px), (orientation: portrait) and (max-width: 1100px)';
const SHEET_COLLAPSED_HEIGHT = 150;

function isMobileSheet() {
  if (!window.matchMedia) return false;
  return window.matchMedia(MOBILE_SHEET_QUERY).matches;
}

// 移动端底部浮层：整层可拖动，上拖增高（上限为页面 1/3），松手后吸附到折叠/展开
function useBottomSheet(setExpanded: React.Dispatch<React.SetStateAction<boolean>>) {
  const drag = useRef<{ startY: number; startHeight: number; mode: 'pending' | 'sheet' | 'scroll' } | null>(null);
  const dragHeightRef = useRef<number | null>(null);
  const [dragHeight, setDragHeight] = useState<number | null>(null);
  const lastDragAt = useRef(0);

  const onTouchStart = (event: React.TouchEvent<HTMLElement>) => {
    if (!isMobileSheet()) return;
    drag.current = {
      startY: event.touches[0].clientY,
      startHeight: event.currentTarget.getBoundingClientRect().height,
      mode: 'pending',
    };
  };

  const onTouchMove = (event: React.TouchEvent<HTMLElement>) => {
    const state = drag.current;
    if (!state) return;
    const delta = state.startY - event.touches[0].clientY;
    if (state.mode === 'pending') {
      if (Math.abs(delta) < 8) return;
      // 展开态且内容已滚动时，让位给原生滚动
      state.mode = event.currentTarget.scrollTop > 0 ? 'scroll' : 'sheet';
    }
    if (state.mode !== 'sheet') return;
    const maxHeight = window.innerHeight / 3;
    const height = Math.min(maxHeight, Math.max(SHEET_COLLAPSED_HEIGHT, state.startHeight + delta));
    dragHeightRef.current = height;
    setDragHeight(height);
  };

  const endDrag = () => {
    const state = drag.current;
    drag.current = null;
    const height = dragHeightRef.current;
    dragHeightRef.current = null;
    setDragHeight(null);
    if (!state || state.mode !== 'sheet' || height === null) return;
    lastDragAt.current = Date.now();
    const maxHeight = window.innerHeight / 3;
    setExpanded(height > (SHEET_COLLAPSED_HEIGHT + maxHeight) / 2);
  };

  const onClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!isMobileSheet() || Date.now() - lastDragAt.current < 250) return;
    if ((event.target as HTMLElement).closest('button, a, select, input, textarea, label')) return;
    setExpanded((current) => !current);
  };

  return {
    dragHeight,
    sheetProps: { onTouchStart, onTouchMove, onTouchEnd: endDrag, onTouchCancel: endDrag, onClick },
  };
}

function sheetClassName(base: string, expanded: boolean, dragHeight: number | null) {
  return `${base} ${expanded ? 'expanded' : ''} ${dragHeight !== null ? 'dragging' : ''}`;
}

function Attribute({ label, value, hint }: { label: string; value: number; hint: string }) {
  return (
    <div className="attribute-row" title={hint}>
      <span>{label}</span>
      <div><i style={{ width: `${value * 10}%` }} /></div>
      <strong>{value}</strong>
    </div>
  );
}

function routePairKey(originId: string, destinationId: string) {
  return [originId, destinationId].sort().join('-');
}

export function Inspector(props: Props) {
  const { game, selectedAirportId, selectedRouteId, selectedAiRouteId } = props;
  const route = selectedRouteId ? game.routes.find((item) => item.id === selectedRouteId) : undefined;
  if (route) return <RouteInspector {...props} routeId={route.id} />;
  if (selectedAiRouteId) {
    const [managerId, routeId] = selectedAiRouteId.split(':');
    if (managerId && routeId) return <AiRouteInspector {...props} managerId={managerId} routeId={routeId} />;
  }
  if (selectedAirportId) return <AirportInspector {...props} airportId={selectedAirportId} />;

  const report = game.lastReport;
  const steps = tutorialSteps(game);
  const showTutorial = !game.tutorialCompleted;
  const [expanded, setExpanded] = useState(false);
  const { dragHeight, sheetProps } = useBottomSheet(setExpanded);

  return (
    <aside
      className={sheetClassName('inspector overview-inspector', expanded, dragHeight)}
      style={dragHeight !== null ? { height: dragHeight } : undefined}
      {...sheetProps}
    >
      <span className="eyebrow">运营总览</span>
      <h2>{game.aircraft.length ? '下一笔钱投向哪里？' : '先购买第一架飞机'}</h2>
      {showTutorial && !game.aircraft.length ? (
        <div className="tutorial-callout">
          <span>01</span>
          <div><strong>启动资本不是利润</strong><p>用 $40M 购买第一架飞机，并保留足够的周转现金。</p></div>
        </div>
      ) : showTutorial && !game.routes.some((item) => item.active) ? (
        <div className="tutorial-callout">
          <span>02</span>
          <div><strong>从香港规划首条航线</strong><p>点击地图上的香港，比较不同目的地的投入、载客率和回本时间。</p></div>
        </div>
      ) : (
        <>
          <div className="overview-grid">
            <div><span>飞机</span><strong>{game.aircraft.length}</strong></div>
            <div><span>航线</span><strong>{game.routes.filter((item) => item.active).length}</strong></div>
            <div><span>枢纽</span><strong>{game.hubs.length}</strong></div>
            <div><span>贷款</span><strong>{game.loans.length}</strong></div>
          </div>
          {report?.signals.map((signal) => (
            <div className={`signal ${signal.tone}`} key={signal.id}>
              <i />
              <div><strong>{signal.title}</strong><p>{signal.detail}</p></div>
            </div>
          ))}
          {game.hubs.length > 0 && (
            <section className="inspector-section">
              <h3>枢纽网络</h3>
              <p className="formula-note">航线只能从枢纽发往其他城市。连接同一枢纽的多条航线之间会自动形成中转连接，为两段航段带来额外旅客和收入。</p>
              <div className="summary-list">
                <div><span>枢纽数量</span><strong>{game.hubs.length}</strong></div>
                <div><span>每周总成本</span><strong>{money(game.hubs.reduce((s, h) => s + h.weeklyCost, 0))}</strong></div>
              </div>
            </section>
          )}
        </>
      )}
      {showTutorial && (
        <div className="tutorial-checklist" aria-label="新手任务链">
          {steps.map((step, index) => (
            <div className={step.done ? 'done' : ''} key={step.label}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <strong>{step.label}</strong>
            </div>
          ))}
        </div>
      )}
      <div className="inspector-tip">点击机场查看市场；点击航线查看真实运力、利润和未满足需求。</div>
    </aside>
  );
}

function AirportInspector({ game, airportId, routeOriginId, routeAircraftId, onBeginRoute, onCancelRoute, onRouteAircraftChange, onBuildHub, onViewRoutes, onRouteSelect, onAiRouteSelect }: Props & { airportId: string }) {
  const airport = airportById(airportId);
  const isHub = game.hubs.some((hub) => hub.airportId === airportId);
  const hub = game.hubs.find((item) => item.airportId === airportId);
  const isConnected = game.routes.some((route) => route.active && [route.originId, route.destinationId].includes(airportId));
  const idleAircraft = idleDeliveredAircraft(game);
  const selectedPlanningAircraft = idleAircraft.find((aircraft) => aircraft.id === routeAircraftId) ?? idleAircraft[0];
  const selectedPlanningType = selectedPlanningAircraft ? aircraftType(selectedPlanningAircraft.typeId) : undefined;
  const idleCount = idleAircraft.length;
  const activeRoutes = game.routes.filter((route) => route.active && [route.originId, route.destinationId].includes(airportId));
  const competitorRoutes = game.aiManagers
    ?.filter((manager) => !manager.bankrupt)
    .flatMap((manager) => manager.routes
      .filter((route) => route.active && (route.originId === airportId || route.destinationId === airportId))
      .map((route) => {
        const otherId = route.originId === airportId ? route.destinationId : route.originId;
        return { manager, route, other: airportById(otherId) };
      })) ?? [];
  const [expanded, setExpanded] = useState(false);
  const { dragHeight, sheetProps } = useBottomSheet(setExpanded);

  useEffect(() => { setExpanded(false); }, [airportId]);

  return (
    <aside
      className={`${sheetClassName('inspector', expanded, dragHeight)} ${routeOriginId === airportId ? 'route-planning-active' : ''}`}
      style={dragHeight !== null ? { height: dragHeight } : undefined}
      {...sheetProps}
    >
      <header className="airport-title">
        <div className={`airport-symbol ${isHub ? 'hub' : ''}`}>{airport.iata.slice(0, 1)}</div>
        <div><span>{airport.iata} · {airport.region}{airport.country !== '中国' ? ` · ${airport.country}` : ''}</span><h2>{airport.nameZh}</h2><p>{airport.nameEn}</p></div>
      </header>
      <div className="tag-row">
        {isHub && <span className="tag accent">公司枢纽</span>}
        <span className="tag">跑道 L{airport.runwayClass}</span>
        <span className="tag">可靠性 {Math.round(airport.reliability * 100)}%</span>
      </div>
      <section className="inspector-section">
        <h3>市场画像</h3>
        <Attribute label="市场规模" value={airport.market} hint="第一版经济规模，作为需求生成的核心基准" />
        <Attribute label="商务指数" value={airport.business} hint="影响商务策略与稳定需求" />
        <Attribute label="旅游指数" value={airport.tourism} hint="影响旺季波动" />
        <Attribute label="国际化" value={airport.international} hint="影响跨区域需求" />
      </section>
      <section className="inspector-section summary-list">
        <div><span>机场费用</span><strong>{airport.feeIndex > 1.45 ? '高' : airport.feeIndex > 1 ? '中' : '低'}</strong></div>
        <div><span>每周时刻容量</span><strong>{integer(airport.weeklyMovements)} 架次</strong></div>
        <div><span>当前公司航线</span><strong>{activeRoutes.length} 条</strong></div>
      </section>
      {isHub && (() => {
        const spokes = game.routes.filter((r) => r.active && r.originId === airportId || r.destinationId === airportId);
        const otherSpokeIds = new Set(spokes.flatMap((r) => [r.originId, r.destinationId].filter((id) => id !== airportId)));
        return (
          <section className="inspector-section">
            <h3>枢纽功能</h3>
            <div className="summary-list">
              <div><span>枢纽类型</span><strong>{hub ? HUB_SPECIALIZATION_LABELS[hub.specialization] : '-'}</strong></div>
              <div><span>每周成本</span><strong>{hub ? money(hub.weeklyCost) : '-'}</strong></div>
              <div><span>辐射城市</span><strong>{otherSpokeIds.size} 个</strong></div>
            </div>
            <p className="formula-note">本枢纽的辐射城市之间会自动形成中转连接，为两段航线增加中转旅客和收入。</p>
          </section>
        );
      })()}
      {competitorRoutes.length > 0 && (
        <section className="inspector-section">
          <h3>竞争对手航线 · {competitorRoutes.length}</h3>
          <div className="competitor-route-list">
            {competitorRoutes.map((item) => {
              const loadFactor = item.route.stats.seats > 0 ? item.route.stats.passengers / item.route.stats.seats : 0;
              const ownRoute = game.routes.find((route) => route.active && routePairKey(route.originId, route.destinationId) === routePairKey(item.route.originId, item.route.destinationId));
              return (
                <button
                  type="button"
                  key={`${item.manager.id}-${item.route.id}`}
                  className="competitor-route-row"
                  onClick={() => onAiRouteSelect?.(item.manager.id, item.route.id)}
                >
                  <span className="competitor-route-airports">
                    {airport.nameZh} ↔ {item.other.nameZh}
                    {ownRoute && (
                      <span
                        className="route-linked-icon"
                        role="button"
                        tabIndex={0}
                        title="查看自己的重合航线"
                        aria-label="查看自己的重合航线"
                        onClick={(event) => {
                          event.stopPropagation();
                          onRouteSelect?.(ownRoute.id);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            event.stopPropagation();
                            onRouteSelect?.(ownRoute.id);
                          }
                        }}
                      >
                        <img src="/icons/route-competition.svg" alt="有竞争" />
                      </span>
                    )}
                  </span>
                  <span className="competitor-route-meta">{item.manager.name} · {item.route.aircraftIds.length} 架 · 载客 {(loadFactor * 100).toFixed(0)}%</span>
                </button>
              );
            })}
          </div>
        </section>
      )}
      {isHub && routeOriginId === airportId && idleAircraft.length > 0 && (
        <label className="route-aircraft-selector">
          <span>规划飞机</span>
          <select aria-label="规划飞机" value={routeAircraftId ?? idleAircraft[0]?.id ?? ''} onChange={(event) => onRouteAircraftChange(event.target.value)}>
            {idleAircraft.map((aircraft) => {
              const type = aircraftType(aircraft.typeId);
              return <option key={aircraft.id} value={aircraft.id}>{type.name} · {Math.round(aircraft.condition * 100)}%</option>;
            })}
          </select>
          {selectedPlanningType && (
            <div className="route-aircraft-range">
              <span>可飞距离</span>
              <strong>{kilometers(selectedPlanningType.rangeKm)}</strong>
            </div>
          )}
        </label>
      )}
      <div className="airport-actions">
        {isHub && (
          <button data-tutorial-begin-route className={`primary-button route-planning-button ${routeOriginId === airportId ? 'danger-soft' : ''}`} disabled={!idleCount && routeOriginId !== airportId} onClick={() => routeOriginId === airportId ? onCancelRoute() : onBeginRoute(airportId)}>
            {routeOriginId === airportId ? '取消航线规划' : idleCount ? `规划新航线 · ${idleCount}架空闲` : '没有空闲飞机'}
          </button>
        )}
        {!routeOriginId && activeRoutes.length > 0 && (
          <button className="primary-button secondary" onClick={() => onViewRoutes(airportId)}>
            查看航线 · {activeRoutes.length}条
          </button>
        )}
        {!isHub && isConnected && (
          <button className="primary-button secondary" onClick={() => onBuildHub(airportId)}>
            建设枢纽 · {money(hubBuildCost(airport))}
          </button>
        )}
      </div>
      {!isHub && !isConnected && <div className="formula-note">先从现有枢纽开通航线，之后才能在此建设新枢纽。</div>}
    </aside>
  );
}

function RouteInspector({ game, routeId, onCloseRoute, onAssignAircraft, onUnassignAircraft, onSetRouteFareRatio, onViewAircraft, onAirportSelect, onAiRouteSelect }: Props & { routeId: string }) {
  const route = game.routes.find((item) => item.id === routeId)!;
  const [expanded, setExpanded] = useState(false);
  const origin = airportById(route.originId);
  const destination = airportById(route.destinationId);
  const stats = route.stats;
  const opponentRoutes = game.aiManagers
    ?.filter((manager) => !manager.bankrupt)
    .flatMap((manager) => manager.routes
      .filter((candidate) => candidate.active && candidate.aircraftIds.length > 0 && routePairKey(candidate.originId, candidate.destinationId) === routePairKey(route.originId, route.destinationId))
      .map((candidate) => ({ manager, route: candidate }))) ?? [];
  const firstOpponentRoute = opponentRoutes[0];
  const idleCompatible = game.aircraft.filter((aircraft) => {
    if (aircraft.routeIds.length) return false;
    if (!isAircraftDelivered(aircraft, game.week)) return false;
    const type = aircraftType(aircraft.typeId);
    return type.rangeKm >= stats.distanceKm && type.runwayClass <= origin.runwayClass && type.runwayClass <= destination.runwayClass;
  });

  useEffect(() => { setExpanded(false); }, [routeId]);

  const { dragHeight, sheetProps } = useBottomSheet(setExpanded);
  const fareValue = Math.min(400, Math.max(0, Math.round(route.pendingFareRatio ?? route.fareRatio ?? 100)));

  return (
    <aside
      className={sheetClassName('inspector route-inspector', expanded, dragHeight)}
      style={dragHeight !== null ? { height: dragHeight } : undefined}
      {...sheetProps}
    >
      <span className="eyebrow">航线经营</span>
      <h2 className="route-title">
        <button type="button" className="route-airport-name" onClick={() => onAirportSelect?.(route.originId)}>{origin.nameZh}</button>
        {' '}<b>↔</b>{' '}
        <button type="button" className="route-airport-name" onClick={() => onAirportSelect?.(route.destinationId)}>{destination.nameZh}</button>
        {firstOpponentRoute && (
          <button type="button" className="route-competition-link" onClick={() => onAiRouteSelect?.(firstOpponentRoute.manager.id, firstOpponentRoute.route.id)} title="查看竞争对手航线">
            <img src="/icons/route-competition.svg" alt="有竞争" />
          </button>
        )}
      </h2>
      <p className="route-subtitle">{route.originId}-{route.destinationId} · {kilometers(stats.distanceKm)} · 培育 {route.maturityWeeks} 周</p>
      <div className={`load-gauge ${stats.loadFactor >= 0.98 ? 'tight' : ''}`}>
        <div className="gauge-head"><span>真实载客率</span><strong>{percent(stats.loadFactor, 1)}</strong></div>
        <div className="gauge-track"><i style={{ width: `${stats.loadFactor * 100}%` }} /></div>
        <p>{integer(stats.passengers)} 名航段旅客 / {integer(stats.seats)} 个座位</p>
      </div>
      <div className="route-kpis">
        <div><span>本地旅客</span><strong>{integer(stats.localPassengers)}</strong></div>
        <div><span>中转旅客</span><strong>{integer(stats.transferPassengers)}</strong></div>
        <div><span>未满足需求</span><strong className={stats.unmetDemand ? 'gold' : ''}>{integer(stats.unmetDemand)}</strong></div>
        <div><span>每周往返</span><strong>{integer(stats.roundTrips)} 班</strong></div>
        <div><span>周收入</span><strong>{money(stats.revenue)}</strong></div>
        <div><span>经营利润</span><strong className={stats.operatingProfit < 0 ? 'red' : 'green'}>{money(stats.operatingProfit)}</strong></div>
      </div>
      <section className="inspector-section">
        <h3>执飞飞机 · {route.aircraftIds.length}</h3>
        {route.aircraftIds.map((aircraftId) => {
          const aircraft = game.aircraft.find((item) => item.id === aircraftId)!;
          const type = aircraftType(aircraft.typeId);
          return (
            <div className="mini-aircraft managed" key={aircraftId}>
              <i style={{ background: type.accent }} />
              <button type="button" className="route-aircraft-info" onClick={() => onViewAircraft?.(aircraftId)} aria-label={`调整 ${type.name}`}>
                <span>{type.name}<small>{type.cabins.economy}Y/{type.cabins.business}J/{type.cabins.first}F · 上限{type.maxEconomySeats}</small></span>
              </button>
              <b>{Math.round(aircraft.condition * 100)}%</b>
              <button aria-label={`撤下 ${type.name}`} onClick={() => onUnassignAircraft(route.id, aircraftId)}>撤下</button>
            </div>
          );
        })}
        {idleCompatible.map((aircraft) => (
          <div className="add-aircraft-row" key={aircraft.id}>
            <button className="add-aircraft-button" onClick={() => onAssignAircraft(route.id, aircraft.id)}><Plus weight="bold" size={14} /> 增派 {aircraftType(aircraft.typeId).name}</button>
          </div>
        ))}
      </section>
      {onSetRouteFareRatio && (
        <section className="inspector-section">
          <h3>票价设定</h3>
          <label className="route-fare-input">
            <span>相对基准价的百分比 {fareValue}%</span>
            <div>
              <input
                type="range"
                min={0}
                max={400}
                step={5}
                value={fareValue}
                onChange={(event) => onSetRouteFareRatio(route.id, Number(event.target.value))}
                aria-label={`${route.originId}-${route.destinationId} 航线票价`}
              />
              <b>{fareValue}%</b>
            </div>
          </label>
          {route.pendingFareRatio !== undefined && <p className="formula-note">下周生效，当前结算票价 {Math.round(route.fareRatio ?? 100)}%。</p>}
          <div className="route-fare-strategy">
            {[
              { label: '低价', ratio: 65 },
              { label: '标准', ratio: 100 },
              { label: '高价', ratio: 140 },
            ].map(({ label, ratio }) => (
              <button
                key={ratio}
                type="button"
                className={(route.pendingFareRatio ?? route.fareRatio ?? 100) === ratio ? 'active' : ''}
                onClick={() => onSetRouteFareRatio(route.id, ratio)}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="formula-note">
            低于 30% 时不再保留最低基础客流；AI 会根据现金余力与策略回应价格战。口碑越好越能支撑高价。
          </p>
        </section>
      )}
      <button
        className="text-danger-button"
        onClick={() => {
          if (!window.confirm(`确认关闭航线 ${route.originId}-${route.destinationId}？这会释放执飞机，并退回部分押金。`)) return;
          onCloseRoute(route.id);
        }}
      >
        关闭航线并释放飞机
      </button>
      <div className="formula-note">中转旅客会同时占用两段座位；本页载客率由航段容量硬限制，最高为100%。</div>
    </aside>
  );
}


const AI_STRATEGY_LABELS: Record<AiStrategy, string> = {
  basic: '基础型',
  advanced: '进阶型',
  strategic: '战略型',
  elite: '精英型',
};

const AI_TACTIC_LABELS: Record<AiRouteTactic, string> = {
  yield_management: '收益管理',
  price_war: '价格战',
  capacity_pressure: '运力压制',
  market_probe: '市场探测',
};

function AiRouteInspector({ game, managerId, routeId, onAirportSelect, onRouteSelect, onOpenRoute }: Props & { managerId: string; routeId: string }) {
  const manager = game.aiManagers?.find((item) => item.id === managerId);
  const route = manager?.routes.find((item) => item.id === routeId);
  const [expanded, setExpanded] = useState(false);
  const { dragHeight, sheetProps } = useBottomSheet(setExpanded);

  useEffect(() => { setExpanded(false); }, [managerId, routeId]);

  if (!manager || !route) return null;

  const origin = airportById(route.originId);
  const destination = airportById(route.destinationId);
  const stats = route.stats;
  const headquarters = airportById(manager.headquartersId);
  const activeRoutes = manager.routes.filter((item) => item.active);
  const ownRoute = game.routes.find((item) => item.active && routePairKey(item.originId, item.destinationId) === routePairKey(route.originId, route.destinationId));
  const hubOriginId = game.hubs.some((hub) => hub.airportId === route.originId)
    ? route.originId
    : game.hubs.some((hub) => hub.airportId === route.destinationId) ? route.destinationId : undefined;
  const quickRouteDestinationId = hubOriginId === route.originId ? route.destinationId : route.originId;
  const quickRouteAircraft = hubOriginId && !ownRoute
    ? idleDeliveredAircraft(game).find((aircraft) => {
      const type = aircraftType(aircraft.typeId);
      const quickOrigin = airportById(hubOriginId);
      const quickDestination = airportById(quickRouteDestinationId);
      return type.rangeKm >= stats.distanceKm && type.runwayClass <= quickOrigin.runwayClass && type.runwayClass <= quickDestination.runwayClass && game.cash >= routeLaunchCost(quickOrigin, quickDestination);
    })
    : undefined;
  const memory = manager.routeMemory?.[route.id];

  return (
    <aside
      className={sheetClassName('inspector', expanded, dragHeight)}
      style={dragHeight !== null ? { height: dragHeight } : undefined}
      {...sheetProps}
    >
      <span className="eyebrow">竞争对手</span>
      <h2 className="route-title">
        <button type="button" className="route-airport-name" onClick={() => onAirportSelect?.(route.originId)}>{origin.nameZh}</button>
        {' '}<b>↔</b>{' '}
        <button type="button" className="route-airport-name" onClick={() => onAirportSelect?.(route.destinationId)}>{destination.nameZh}</button>
        {ownRoute && (
          <button type="button" className="route-competition-link" onClick={() => onRouteSelect?.(ownRoute.id)} title="查看自己的重合航线">
            <img src="/icons/route-competition.svg" alt="有竞争" />
          </button>
        )}
      </h2>
      <p className="route-subtitle">{route.originId}-{route.destinationId} · {kilometers(stats.distanceKm)} · 培育 {route.maturityWeeks} 周</p>
      <div className="tag-row">
        <span className="tag accent">{manager.name}</span>
        <span className="tag">{AI_STRATEGY_LABELS[manager.strategy]}</span>
        {route.aiTactic && <span className="tag">{AI_TACTIC_LABELS[route.aiTactic]}</span>}
      </div>
      <div className={`load-gauge ${stats.loadFactor >= 0.98 ? 'tight' : ''}`}>
        <div className="gauge-head"><span>真实载客率</span><strong>{percent(stats.loadFactor, 1)}</strong></div>
        <div className="gauge-track"><i style={{ width: `${stats.loadFactor * 100}%` }} /></div>
        <p>{integer(stats.passengers)} 名航段旅客 / {integer(stats.seats)} 个座位</p>
      </div>
      <div className="route-kpis">
        <div><span>本地旅客</span><strong>{integer(stats.localPassengers)}</strong></div>
        <div><span>中转旅客</span><strong>{integer(stats.transferPassengers)}</strong></div>
        <div><span>未满足需求</span><strong className={stats.unmetDemand ? 'gold' : ''}>{integer(stats.unmetDemand)}</strong></div>
        <div><span>每周往返</span><strong>{integer(stats.roundTrips)} 班</strong></div>
        <div><span>周收入</span><strong>{money(stats.revenue)}</strong></div>
        <div><span>经营利润</span><strong className={stats.operatingProfit < 0 ? 'red' : 'green'}>{money(stats.operatingProfit)}</strong></div>
      </div>
      {memory && (
        <section className="inspector-section summary-list">
          <h3>AI 运营记忆</h3>
          <div><span>观察周数</span><strong>{memory.weeksObserved} 周</strong></div>
          <div><span>近期均收</span><strong>{money(memory.trailingRevenue)}</strong></div>
          <div><span>近期均利</span><strong className={memory.trailingOperatingProfit < 0 ? 'red' : 'green'}>{money(memory.trailingOperatingProfit)}</strong></div>
          <div><span>近期客座率</span><strong>{percent(memory.trailingLoadFactor)}</strong></div>
        </section>
      )}
      <section className="inspector-section summary-list">
        <h3>公司概况</h3>
        <div><span>总部</span><strong>{headquarters.nameZh}</strong></div>
        <div><span>机队</span><strong>{manager.aircraft.length} 架</strong></div>
        <div><span>航线</span><strong>{activeRoutes.length} 条</strong></div>
        <div><span>枢纽</span><strong>{manager.hubs.length} 个</strong></div>
        <div><span>现金</span><strong>{money(manager.cash)}</strong></div>
        <div><span>声誉</span><strong>{Math.round(manager.reputation)}</strong></div>
      </section>
      {hubOriginId && quickRouteAircraft && (
        <div className="inspector-quick-action">
          <button className="primary-button" type="button" onClick={() => onOpenRoute?.(hubOriginId, quickRouteDestinationId, quickRouteAircraft.id)}>
            一键建立航线
          </button>
          <small>从{airportById(hubOriginId).nameZh}出发 · 使用{aircraftType(quickRouteAircraft.typeId).name}</small>
        </div>
      )}
      <div className="formula-note">对手数据为模拟估算，仅反映其公开运力与收益水平。</div>
    </aside>
  );
}
